// ConfirmationDialogContext.tsx
import Button, { ButtonProps } from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import React, {
 createContext,
 ReactNode,
 useCallback,
 useContext,
 useMemo,
 useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import CloseButton from '../components/common/CloseButton';
import LazySvg from '../components/common/LazySvg';
import customColors from '../utils/customColors';

// Define types for dialog state and the context function
interface ConfirmationDialogProps {
 isOpen: boolean;
 title: string;
 message: string;
 buttonText?: string;
 buttonProps?: ButtonProps;
 onConfirm: () => void;
}

interface ConfirmationDialogContextType {
 openDialog: ({
  title,
  message,
  buttonText,
  buttonProps,
  onConfirm,
 }: Omit<ConfirmationDialogProps, 'isOpen'>) => void;
}

// Create the context with proper types
const ConfirmationDialogContext = createContext<
 ConfirmationDialogContextType | undefined
>(undefined);

export const ConfirmationDialogProvider = ({
 children,
}: {
 children: ReactNode;
}) => {
 const { t } = useTranslation();
 const [dialogState, setDialogState] = useState<ConfirmationDialogProps>({
  isOpen: false,
  title: '',
  message: '',
  buttonText: '',
  buttonProps: {},
  onConfirm: () => {},
 });

 const openDialog = useCallback(
  ({
   title,
   message,
   buttonText,
   buttonProps,
   onConfirm,
  }: Omit<ConfirmationDialogProps, 'isOpen'>) => {
   setDialogState({
    isOpen: true,
    title,
    message,
    buttonText,
    buttonProps,
    onConfirm,
   });
  },
  [],
 );

 const closeDialog = useCallback(() => {
  setDialogState((prevState) => ({ ...prevState, isOpen: false }));
 }, []);

 const confirmAction = useCallback(() => {
  if (dialogState.onConfirm) dialogState.onConfirm();
  closeDialog();
 }, [dialogState.onConfirm, closeDialog]);

 // Memoize the value for the context provider
 const contextValue = useMemo(
  () => ({
   openDialog,
  }),
  [openDialog],
 );

 return (
  <ConfirmationDialogContext.Provider value={contextValue}>
   {children}
   {dialogState.isOpen && (
    <Dialog
     open={dialogState.isOpen}
     onClose={closeDialog}
     sx={{
      '& .MuiDialog-paper': {
       maxWidth: 408,
      },
     }}
    >
     <CloseButton closeDialog={closeDialog} />
     <DialogTitle>{dialogState.title}</DialogTitle>
     <DialogContent>{dialogState.message}</DialogContent>
     <DialogActions>
      <Button onClick={closeDialog} variant="outlined" color="secondary">
       {t('global.cancel')}
      </Button>
      <Button
       onClick={confirmAction}
       variant="outlined"
       color="error"
       endIcon={
        <LazySvg
         name="delete"
         styles={{ '& path': { stroke: customColors.red } }}
        />
       }
       {...dialogState.buttonProps}
      >
       {dialogState.buttonText || t('global.delete')}
      </Button>
     </DialogActions>
    </Dialog>
   )}
  </ConfirmationDialogContext.Provider>
 );
};

export const useConfirmationDialog = (): ConfirmationDialogContextType => {
 const context = useContext(ConfirmationDialogContext);
 if (!context) {
  throw new Error(
   'useConfirmationDialog must be used within a ConfirmationDialogProvider',
  );
 }
 return context;
};
